import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { useTheme } from '../contexts/AppContexts';
import { ArrowLeft, ChevronDown, ChevronRight, Download, Edit, MessageSquare, Plus, Printer, Save, Trash2, Upload } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { writeSanitizedHTML } from '../utils/safePrint';

const ICSGeneralMessageForm = ({ currentExercise }) => {
  const { theme } = useTheme();
  
  // Complete ICS Positions array - same as Organization List
  const positions = [
    'Command - Incident Commander',
    'Command - Incident Commander (deputy)',
    'Command - Safety Officer',
    'Command - Information Officer',
    'Command - Liaison Officer',
    'Operations - Section Chief',
    'Operations - Air Ops Branch Director',
    'Operations - Branch 1 Director',
    'Operations - Branch 2 Director',
    'Operations - Air Support Group Supervisor',
    'Operations - Helibase Supervisor',
    'Operations - Helispot Supervisor',
    'Operations - Fixed-Wing Coordinator',
    'Operations - Air Tactical Group Supervisor',
    'Operations - Helicopter Coord',
    'Operations - Fixed-Wing Coord (Tac)',
    'Operations - Air Tanker Coord',
    'Operations - Division Group 1a Supervisor',
    'Operations - Division Group 1b Supervisor',
    'Operations - Division Group 1c Supervisor',
    'Operations - Division Group 1d Supervisor',
    'Operations - Division Group 2a Supervisor',
    'Operations - Division Group 2b Supervisor',
    'Operations - Division Group 2c Supervisor',
    'Operations - Division Group 2d Supervisor',
    'Planning - Section Chief',
    'Planning - Resources Unit - Leader',
    'Planning - Situation - Leader',
    'Planning - Documentation - Leader',
    'Planning - Demobilization Unit - Leader',
    'Planning - Tech Special',
    'Logistics - Section Chief',
    'Logistics - Branch Support - Director',
    'Logistics - Supply Unit - Leader',
    'Logistics - Facilities Unit - Leader',
    'Logistics - Ground Support Unit - Leader',
    'Logistics - Branch Services - Director',
    'Logistics - Communications Unit - Director',
    'Logistics - Medical Unit - Leader',
    'Logistics - Food Unit - Leader',
    'Finance - Section Chief',
    'Finance - Time Unit - Leader',
    'Finance - Procurement Unit - Leader',
    'Finance - Compensation/Claims Unit - Leader',
    'Finance - Cost Unit - Leader',
    'Exercise Evaluator',
    'Exercise Director',
    'Exercise Observer',
    'Team Coordinator',
    'Team Planner',
    'Team Facilitator',
    'Team Evaluator',
    'Team Task',
    'Participant',
    'Staff Member',
    'Fire Chief',
    'Police Chief',
    'Fire Fighter',
    'Medical',
    'Supervisor',
    'Security',
    'Facilitator',
    'Umpire',
    'Scribe',
    'Assistant',
    'Exercise Controller',
    'Agency Representative',
    'Owner',
    'Actor',
    'VIP',
    'Media',
    'Visitor'
  ];

  // Category & Priority (aligned with Activity Log)
  const EVENT_CATEGORIES = [
    'Decision',
    'Action',
    'Information Only',
    'Communication',
    'Resource Request',
    'Situation Update',
    'Objective Set',
    'Safety Concern',
    'Escalation',
    'Status Change',
    'Other',
  ];
  const CATEGORY_STYLES = {
    'Decision':          'bg-purple-500/20 text-purple-300 border-purple-500/40',
    'Action':            'bg-blue-500/20 text-blue-300 border-blue-500/40',
    'Information Only':  'bg-gray-500/20 text-gray-300 border-gray-500/40',
    'Communication':     'bg-cyan-500/20 text-cyan-300 border-cyan-500/40',
    'Resource Request':  'bg-amber-500/20 text-amber-300 border-amber-500/40',
    'Situation Update':  'bg-teal-500/20 text-teal-300 border-teal-500/40',
    'Objective Set':     'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
    'Safety Concern':    'bg-red-500/20 text-red-300 border-red-500/40',
    'Escalation':        'bg-orange-500/20 text-orange-300 border-orange-500/40',
    'Status Change':     'bg-indigo-500/20 text-indigo-300 border-indigo-500/40',
    'Other':             'bg-gray-500/20 text-gray-300 border-gray-500/40',
  };
  const PRIORITY_LEVELS = ['Critical', 'High', 'Medium', 'Low'];
  const PRIORITY_STYLES = {
    'Critical': 'bg-red-500/20 text-red-300 border-red-500/50',
    'High':     'bg-orange-500/20 text-orange-300 border-orange-500/50',
    'Medium':   'bg-amber-500/20 text-amber-300 border-amber-500/50',
    'Low':      'bg-green-500/20 text-green-300 border-green-500/50',
  };
  const PRIORITY_BORDER = {
    'Critical': 'border-l-4 border-l-red-500',
    'High':     'border-l-4 border-l-orange-500',
    'Medium':   'border-l-4 border-l-amber-500',
    'Low':      'border-l-4 border-l-green-500',
  };
  const PRIORITY_TEXT = {
    'Critical': 'text-red-400',
    'High':     'text-orange-400',
    'Medium':   'text-amber-400',
    'Low':      'text-green-400',
  };

  const [collapsedMessages, setCollapsedMessages] = useState(new Set());
  const toggleMessageCollapsed = (id) => {
    setCollapsedMessages(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const expandAllMessages = () => setCollapsedMessages(new Set());
  const collapseAllMessages = () => setCollapsedMessages(new Set(messages.map(m => m.id)));

  const [view, setView] = useState('list'); // 'list', 'form'
  const [editingMessage, setEditingMessage] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch messages from backend API
  useEffect(() => {
    if (!currentExercise?.id) return;
    const fetchMessages = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API_BASE_URL}/api/ics-213/${currentExercise.id}`);
        setMessages(response.data);
      } catch (error) {
        console.error('Error fetching ICS 213 messages:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchMessages();
  }, [currentExercise?.id]);

  const [formData, setFormData] = useState({
    incidentName: '',
    to: '',
    from: '',
    subject: '',
    date: '',
    time: '',
    category: 'Information Only',
    priority: 'Medium',
    message: '',
    approvedByName: '',
    approvedBySignature: '',
    approvedByPosition: '',
    reply: '',
    repliedByName: '',
    repliedBySignature: '',
    repliedByPosition: '',
    repliedDateTime: ''
  });

  const resetForm = () => {
    setFormData({
      incidentName: currentExercise?.exercise_name || currentExercise?.name || '',
      to: '',
      from: '',
      subject: '',
      date: '',
      time: '',
      category: 'Information Only',
      priority: 'Medium',
      message: '',
      approvedByName: '',
      approvedBySignature: '',
      approvedByPosition: '',
      reply: '',
      repliedByName: '',
      repliedBySignature: '',
      repliedByPosition: '',
      repliedDateTime: ''
    });
  };

  const handleAddNew = () => {
    resetForm();
    setEditingMessage(null);
    setView('form');
  };

  const fileInputRef = useRef(null);

  const handleExportCSV = async () => {
    if (!currentExercise?.id) return;
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/ics-213/${currentExercise.id}/export-csv`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      const safeName = (currentExercise.exercise_name || currentExercise.name || 'exercise').replace(/[^a-z0-9]+/gi, '_');
      link.setAttribute('download', `ics-213-${safeName}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      window.alert('Failed to export CSV. Please try again.');
    }
  };

  // WinLink-compatible column order (must match backend ICS213_CSV_COLUMNS)
  const ICS213_CSV_HEADERS = [
    'Incident Name',
    'To (Name/Position)',
    'From (Name/Position)',
    'Subject',
    'Date',
    'Time',
    'Message',
    'Approved by (Name)',
    'Position/Title',
    'Category',
    'Priority',
    'Approved Signature',
    'Reply',
    'Replied By Name',
    'Replied By Signature',
    'Replied By Position',
    'Replied Date/Time',
  ];

  const csvEscape = (val) => {
    const s = (val === null || val === undefined) ? '' : String(val);
    // Quote if contains comma, quote, CR, or LF; escape embedded quotes
    if (/[",\r\n]/.test(s)) {
      return '"' + s.replace(/"/g, '""') + '"';
    }
    return s;
  };

  const handleExportSingleCSV = (message) => {
    if (!message) return;
    try {
      const row = [
        message.incidentName || currentExercise?.exercise_name || currentExercise?.name || '',
        message.to || '',
        message.sender || message.from || '',
        message.subject || '',
        message.date || '',
        message.time || '',
        message.message || '',
        message.approvedByName || '',
        message.approvedByPosition || '',
        message.category || 'Information Only',
        message.priority || 'Medium',
        message.approvedBySignature || '',
        message.reply || '',
        message.repliedByName || '',
        message.repliedBySignature || '',
        message.repliedByPosition || '',
        message.repliedDateTime || '',
      ];
      const csv = ICS213_CSV_HEADERS.map(csvEscape).join(',') + '\r\n' + row.map(csvEscape).join(',') + '\r\n';
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeSubject = (message.subject || 'entry').replace(/[^a-z0-9]+/gi, '_').slice(0, 40);
      link.setAttribute('download', `ics-213-${safeSubject}-${(message.id || '').slice(0, 8)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting single message CSV:', error);
      window.alert('Failed to export message. Please try again.');
    }
  };

  const handlePrintAllReport = () => {
    if (!messages || messages.length === 0) {
      window.alert('No messages to print.');
      return;
    }
    const CAT_COLOR = {
      'Decision': '#a855f7',
      'Action': '#3b82f6',
      'Information Only': '#6b7280',
      'Communication': '#06b6d4',
      'Resource Request': '#f59e0b',
      'Situation Update': '#14b8a6',
      'Objective Set': '#10b981',
      'Safety Concern': '#ef4444',
      'Escalation': '#f97316',
      'Status Change': '#6366f1',
      'Other': '#6b7280',
    };
    const PRIO_COLOR = {
      'Critical': '#ef4444',
      'High': '#f97316',
      'Medium': '#f59e0b',
      'Low': '#22c55e',
    };
    const esc = (s) => (s === null || s === undefined ? '' : String(s))
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    const rows = messages.map((m) => {
      const cat = m.category || 'Information Only';
      const prio = m.priority || 'Medium';
      const catColor = CAT_COLOR[cat] || '#6b7280';
      const prioColor = PRIO_COLOR[prio] || '#f59e0b';
      const dateStr = m.date || 'Not specified';
      const timeStr = m.time ? ` at ${esc(m.time)}` : '';
      const sender = m.sender || m.from || 'Not specified';
      const msgHtml = esc(m.message || '').replace(/\n/g, '<br/>');
      return `
        <div class="entry" style="border-left: 6px solid ${prioColor};">
          <div class="entry-header">
            <span class="subject">${esc(m.subject || 'No Subject')}</span>
            <span class="badge ics213">ICS 213</span>
            <span class="badge" style="background:${catColor}22; color:${catColor}; border-color:${catColor}66;">${esc(cat)}</span>
            <span class="badge" style="background:${prioColor}22; color:${prioColor}; border-color:${prioColor}66;">${esc(prio)}</span>
          </div>
          <div class="meta">
            <div><strong>To:</strong> ${esc(m.to || 'Not specified')}</div>
            <div><strong>From:</strong> ${esc(sender)}</div>
            <div><strong>Date:</strong> ${esc(dateStr)}${timeStr}</div>
          </div>
          ${m.message ? `<div class="body">${msgHtml}</div>` : ''}
        </div>
      `;
    }).join('');

    const exName = esc(currentExercise?.exercise_name || currentExercise?.name || 'Exercise');
    const now = esc(new Date().toLocaleString());

    const html = `
      <html>
        <head>
          <title>ICS 213 General Message Report - ${exName}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 24px; background: white; color: #111; }
            h1 { font-size: 20px; margin: 0 0 4px 0; color: #ea580c; }
            .sub { color: #555; font-size: 12px; margin-bottom: 20px; border-bottom: 1px solid #eee; padding-bottom: 12px; }
            .entry { padding: 12px 16px; margin-bottom: 14px; background: #f9fafb; border-radius: 4px; break-inside: avoid; page-break-inside: avoid; border: 1px solid #eee; }
            .entry-header { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 8px; }
            .subject { font-size: 15px; font-weight: 700; margin-right: 4px; }
            .badge { font-size: 10px; padding: 2px 8px; border-radius: 999px; border: 1px solid; font-weight: 600; letter-spacing: 0.02em; text-transform: uppercase; }
            .badge.ics213 { background: #fff7ed; color: #ea580c; border-color: #fdba74; }
            .meta { font-size: 12px; color: #444; margin-bottom: 6px; }
            .meta div { margin: 2px 0; }
            .body { font-size: 13px; color: #222; margin-top: 8px; white-space: pre-wrap; padding-top: 8px; border-top: 1px dashed #ddd; }
            @media print { body { margin: 12px; } .entry { background: #fff; } }
          </style>
        </head>
        <body>
          <h1>ICS 213 General Message Report</h1>
          <div class="sub">Incident: <strong>${exName}</strong> &nbsp;·&nbsp; ${messages.length} message${messages.length === 1 ? '' : 's'} &nbsp;·&nbsp; Generated ${now}</div>
          ${rows}
        </body>
      </html>
    `;

    const w = window.open('', '_blank');
    if (!w) {
      window.alert('Pop-up blocker prevented the print window. Please allow pop-ups.');
      return;
    }
    writeSanitizedHTML(w.document, html);
    setTimeout(() => { try { w.print(); } catch (e) { /* noop */ } }, 400);
  };

  const handleImportCSVClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleImportCSVFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !currentExercise?.id) return;
    try {
      const formPayload = new FormData();
      formPayload.append('file', file);
      const response = await axios.post(
        `${API_BASE_URL}/api/ics-213/${currentExercise.id}/import-csv`,
        formPayload,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const { created = 0, updated = 0 } = response.data || {};
      window.alert(`Import complete. ${created} created, ${updated} updated.`);
      // Refetch
      const refetch = await axios.get(`${API_BASE_URL}/api/ics-213/${currentExercise.id}`);
      setMessages(refetch.data);
    } catch (error) {
      console.error('Error importing CSV:', error);
      window.alert(error?.response?.data?.detail || 'Failed to import CSV. Please check the file format.');
    }
  };

  const handleEdit = (message) => {
    setFormData({ ...message, from: message.sender || message.from || '' });
    setEditingMessage(message);
    setView('form');
  };

  const handleDelete = async (messageId) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/ics-213/${messageId}`);
      setMessages(prev => prev.filter(msg => msg.id !== messageId));
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };

  const handleSave = async () => {
    try {
      if (editingMessage) {
        const response = await axios.put(`${API_BASE_URL}/api/ics-213/${editingMessage.id}`, {
          ...formData,
          sender: formData.from
        });
        setMessages(prev => prev.map(msg => msg.id === editingMessage.id ? response.data : msg));
      } else {
        const response = await axios.post(`${API_BASE_URL}/api/ics-213/${currentExercise.id}`, {
          ...formData,
          sender: formData.from
        });
        setMessages(prev => [response.data, ...prev]);
      }
      setView('list');
      resetForm();
      setEditingMessage(null);
    } catch (error) {
      console.error('Error saving message:', error);
    }
  };

  const handlePrintICS213 = (message = null) => {
    const messageToPrint = message || formData;
    
    const printContent = `
      <html>
        <head>
          <title>ICS 213 - General Message</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; background: white; color: black; }
            .header { text-align: center; font-size: 18px; font-weight: bold; margin-bottom: 20px; }
            .field { margin-bottom: 10px; }
            .field-label { font-weight: bold; display: inline-block; width: 100px; }
            .field-value { border-bottom: 1px solid #000; display: inline-block; min-width: 200px; padding: 2px; }
            .message-box { border: 1px solid #000; padding: 10px; margin: 10px 0; min-height: 100px; }
            .signature-line { border-bottom: 1px solid #000; width: 150px; display: inline-block; margin: 0 10px; }
            .section-title { font-weight: bold; margin: 15px 0 5px 0; }
            @media print { body { margin: 0; } }
          </style>
        </head>
        <body>
          <div class="header">ICS 213 - GENERAL MESSAGE</div>
          <div class="field">
            <span class="field-label">To:</span>
            <span class="field-value">${messageToPrint.to || ''}</span>
          </div>
          <div class="field">
            <span class="field-label">From:</span>
            <span class="field-value">${messageToPrint.sender || messageToPrint.from || ''}</span>
          </div>
          <div class="field">
            <span class="field-label">Subject:</span>
            <span class="field-value">${messageToPrint.subject || ''}</span>
          </div>
          <div class="field">
            <span class="field-label">Date:</span>
            <span class="field-value">${messageToPrint.date || ''}</span>
            <span class="field-label" style="margin-left: 50px;">Time:</span>
            <span class="field-value">${messageToPrint.time || ''}</span>
          </div>
          <div class="section-title">Message:</div>
          <div class="message-box">${messageToPrint.message || ''}</div>
          <div class="section-title">Approved by:</div>
          <div class="field">
            <span class="field-label">Name:</span>
            <span class="field-value">${messageToPrint.approvedByName || ''}</span>
          </div>
          <div class="field">
            <span class="field-label">Signature:</span>
            <span class="signature-line">${messageToPrint.approvedBySignature || ''}</span>
          </div>
          <div class="field">
            <span class="field-label">Position:</span>
            <span class="field-value">${messageToPrint.approvedByPosition || ''}</span>
          </div>
          <div class="section-title">Reply:</div>
          <div class="message-box">${messageToPrint.reply || ''}</div>
          <div class="section-title">Replied by:</div>
          <div class="field">
            <span class="field-label">Name:</span>
            <span class="field-value">${messageToPrint.repliedByName || ''}</span>
          </div>
          <div class="field">
            <span class="field-label">Signature:</span>
            <span class="signature-line">${messageToPrint.repliedBySignature || ''}</span>
          </div>
          <div class="field">
            <span class="field-label">Position:</span>
            <span class="field-value">${messageToPrint.repliedByPosition || ''}</span>
          </div>
          <div class="section-title">Replied:</div>
          <div class="field">
            <span class="field-label">Date/Time:</span>
            <span class="field-value">${messageToPrint.repliedDateTime || ''}</span>
          </div>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    writeSanitizedHTML(printWindow.document, printContent);
    printWindow.print();
  };

  if (view === 'form') {
    return (
      <div className="p-6 pb-24">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Button 
                onClick={() => setView('list')}
                variant="ghost" 
                size="sm"
                className="text-gray-400 hover:text-white"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Messages
              </Button>
            </div>
            <Button
              onClick={() => handlePrintICS213()}
              variant="outline"
              className="border-green-500/50 text-green-400 hover:bg-green-500/10"
            >
              <Printer className="h-4 w-4 mr-2" />
              Print ICS 213
            </Button>
          </div>

          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-orange-500 flex items-center gap-2">
                <MessageSquare className="h-6 w-6" />
                ICS 213 - General Message
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Incident Name (auto from exercise, read-only) */}
              <div>
                <Label className="text-gray-300">Incident Name:</Label>
                <Input
                  value={formData.incidentName || currentExercise?.exercise_name || currentExercise?.name || ''}
                  readOnly
                  className="bg-gray-900 border-gray-600 text-gray-300 cursor-not-allowed"
                  data-testid="ics213-incident-name"
                />
              </div>

              {/* To and From */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">To (Name/Position):</Label>
                  <Input
                    value={formData.to}
                    onChange={(e) => setFormData(prev => ({ ...prev, to: e.target.value }))}
                    placeholder="Name and Position"
                    className="bg-gray-700 border-gray-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">From (Name/Position):</Label>
                  <Input
                    value={formData.from}
                    onChange={(e) => setFormData(prev => ({ ...prev, from: e.target.value }))}
                    placeholder="Name and Position"
                    className="bg-gray-700 border-gray-600 text-white"
                  />
                </div>
              </div>

              {/* Subject */}
              <div>
                <Label className="text-gray-300">Subject:</Label>
                <Input
                  value={formData.subject}
                  onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>

              {/* Category & Priority */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Category:</Label>
                  <Select
                    value={formData.category || 'Information Only'}
                    onValueChange={(val) => setFormData(prev => ({ ...prev, category: val }))}
                  >
                    <SelectTrigger className="bg-gray-700 border-gray-600 text-white w-full" data-testid="ics213-category">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-700 border-gray-600">
                      {EVENT_CATEGORIES.map(cat => (
                        <SelectItem key={cat} value={cat} className="text-white focus:bg-gray-600">{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-300">Priority:</Label>
                  <Select
                    value={formData.priority || 'Medium'}
                    onValueChange={(val) => setFormData(prev => ({ ...prev, priority: val }))}
                  >
                    <SelectTrigger className={`bg-gray-700 border-gray-600 w-full font-semibold ${PRIORITY_TEXT[formData.priority] || 'text-white'}`} data-testid="ics213-priority">
                      <SelectValue placeholder="Select priority" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-700 border-gray-600">
                      {PRIORITY_LEVELS.map(p => (
                        <SelectItem key={p} value={p} className={`${PRIORITY_TEXT[p]} font-semibold focus:bg-gray-600`}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Date / Time */}
              <div>
                <Label className="text-gray-300">Date/Time:</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                    className="bg-gray-700 border-gray-600 text-white"
                  />
                  <Input
                    type="text"
                    placeholder="HH:MM"
                    pattern="[0-2][0-9]:[0-5][0-9]"
                    maxLength="5"
                    value={formData.time}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^0-9:]/g, '');
                      if (value.length === 2 && !value.includes(':')) {
                        value = value + ':';
                      }
                      if (value.length <= 5) {
                        setFormData(prev => ({ ...prev, time: value }));
                      }
                    }}
                    className="bg-gray-700 border-gray-600 text-white"
                  />
                </div>
              </div>

              {/* Message */}
              <div>
                <Label className="text-gray-300">Message:</Label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                  className="bg-gray-700 border-gray-600 text-white w-full min-h-[120px] p-3 rounded-md resize-vertical"
                />
              </div>

              {/* Approved by Section */}
              <div className="bg-gray-750 p-4 rounded-lg">
                <h3 className="text-orange-500 font-semibold mb-4">Approved by:</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-gray-300">Name:</Label>
                    <Input
                      value={formData.approvedByName}
                      onChange={(e) => setFormData(prev => ({ ...prev, approvedByName: e.target.value }))}
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Signature:</Label>
                    <div className="bg-gray-700 border border-gray-600 rounded-md p-2 h-10" style={{width: '150px'}}>
                      <input
                        type="text"
                        value={formData.approvedBySignature}
                        onChange={(e) => setFormData(prev => ({ ...prev, approvedBySignature: e.target.value }))}
                        className="bg-transparent border-none text-white w-full h-full outline-none"
                        style={{borderBottom: '1px solid #666', width: '150px'}}
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-gray-300">Position/Title:</Label>
                    <Select 
                      value={formData.approvedByPosition} 
                      onValueChange={(value) => setFormData(prev => ({ ...prev, approvedByPosition: value }))}
                    >
                      <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                        <SelectValue placeholder="Select position" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-700 border-gray-600">
                        {positions && positions.map((position) => (
                          <SelectItem key={position} value={position} className="text-white focus:bg-gray-600">
                            {position}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Reply */}
              <div>
                <Label className="text-gray-300">Reply:</Label>
                <textarea
                  value={formData.reply}
                  onChange={(e) => setFormData(prev => ({ ...prev, reply: e.target.value }))}
                  className="bg-gray-700 border-gray-600 text-white w-full min-h-[100px] p-3 rounded-md resize-vertical"
                />
              </div>

              {/* Replied by Section */}
              <div className="bg-gray-750 p-4 rounded-lg">
                <h3 className="text-orange-500 font-semibold mb-4">Replied by:</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-gray-300">Name:</Label>
                    <Input
                      value={formData.repliedByName}
                      onChange={(e) => setFormData(prev => ({ ...prev, repliedByName: e.target.value }))}
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Signature:</Label>
                    <div className="bg-gray-700 border border-gray-600 rounded-md p-2 h-10" style={{width: '150px'}}>
                      <input
                        type="text"
                        value={formData.repliedBySignature}
                        onChange={(e) => setFormData(prev => ({ ...prev, repliedBySignature: e.target.value }))}
                        className="bg-transparent border-none text-white w-full h-full outline-none"
                        style={{borderBottom: '1px solid #666', width: '150px'}}
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-gray-300">Position:</Label>
                    <Select 
                      value={formData.repliedByPosition} 
                      onValueChange={(value) => setFormData(prev => ({ ...prev, repliedByPosition: value }))}
                    >
                      <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                        <SelectValue placeholder="Select position" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-700 border-gray-600">
                        {positions && positions.map((position) => (
                          <SelectItem key={position} value={position} className="text-white focus:bg-gray-600">
                            {position}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Replied Date/Time */}
              <div className="bg-gray-750 p-4 rounded-lg">
                <h3 className="text-orange-500 font-semibold mb-4">Replied:</h3>
                <div>
                  <Label className="text-gray-300">Date/Time:</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      type="date"
                      value={formData.repliedDateTime ? formData.repliedDateTime.split('T')[0] : ''}
                      onChange={(e) => {
                        const date = e.target.value;
                        const time = formData.repliedDateTime ? formData.repliedDateTime.split('T')[1] : '00:00';
                        setFormData(prev => ({ ...prev, repliedDateTime: `${date}T${time}` }));
                      }}
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                    <Input
                      type="text"
                      placeholder="HH:MM"
                      pattern="[0-2][0-9]:[0-5][0-9]"
                      maxLength="5"
                      value={formData.repliedDateTime ? formData.repliedDateTime.split('T')[1] : ''}
                      onChange={(e) => {
                        let value = e.target.value.replace(/[^0-9:]/g, '');
                        if (value.length === 2 && !value.includes(':')) {
                          value = value + ':';
                        }
                        if (value.length <= 5) {
                          const date = formData.repliedDateTime ? formData.repliedDateTime.split('T')[0] : '';
                          setFormData(prev => ({ ...prev, repliedDateTime: `${date}T${value}` }));
                        }
                      }}
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button 
                  onClick={handleSave}
                  className="bg-orange-500 hover:bg-orange-600 text-white flex-1"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {editingMessage ? 'Update Message' : 'Save Message'}
                </Button>
                <Button 
                  onClick={() => setView('list')}
                  variant="outline"
                  className="border-gray-600 text-gray-300 hover:bg-gray-700"
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // List view
  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className={`text-3xl font-bold ${theme.colors.textPrimary} flex items-center gap-3`}>
              <MessageSquare className="h-8 w-8 text-orange-500" />
              ICS 213 - General Messages
            </h2>
            <p className={`${theme.colors.textMuted} mt-2`}>
              Manage ICS 213 General Messages for incident communication
            </p>
          </div>
          <div className="flex items-center gap-2">
            {messages.length > 0 && (
              <>
                <Button
                  onClick={expandAllMessages}
                  variant="outline"
                  className="border-gray-600 text-gray-300 hover:bg-gray-700"
                  data-testid="ics213-expand-all"
                >
                  Expand All
                </Button>
                <Button
                  onClick={collapseAllMessages}
                  variant="outline"
                  className="border-gray-600 text-gray-300 hover:bg-gray-700"
                  data-testid="ics213-collapse-all"
                >
                  Collapse All
                </Button>
                <Button
                  onClick={handlePrintAllReport}
                  variant="outline"
                  className="border-green-500/50 text-green-300 hover:bg-green-500/10"
                  data-testid="ics213-print-report"
                  title="Print all entries as a compact report"
                >
                  <Printer className="h-4 w-4 mr-2" />
                  Print ICS 213 Report
                </Button>
              </>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={handleImportCSVFile}
              className="hidden"
              data-testid="ics213-import-file"
            />
            <Button
              onClick={handleImportCSVClick}
              variant="outline"
              className="border-purple-500/50 text-purple-300 hover:bg-purple-500/10"
              data-testid="ics213-import-csv"
              title="Import CSV (WinLink-compatible)"
            >
              <Upload className="h-4 w-4 mr-2" />
              Import CSV
            </Button>
            <Button
              onClick={handleExportCSV}
              variant="outline"
              className="border-cyan-500/50 text-cyan-300 hover:bg-cyan-500/10"
              data-testid="ics213-export-csv"
              disabled={messages.length === 0}
              title="Export CSV (WinLink-compatible)"
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
            <Button
              onClick={handleAddNew}
              className="bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="ics213-new-message"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Message
            </Button>
          </div>
        </div>

        {messages.length === 0 ? (
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-8 text-center">
              <MessageSquare className="h-16 w-16 text-gray-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-300 mb-2">No Messages Yet</h3>
              <p className="text-gray-500 mb-6">Create your first ICS 213 General Message to get started</p>
              <Button 
                onClick={handleAddNew}
                className="bg-orange-500 hover:bg-orange-600 text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create First Message
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {messages.map((message) => {
              const category = message.category || 'Information Only';
              const priority = message.priority || 'Medium';
              const categoryCls = CATEGORY_STYLES[category] || CATEGORY_STYLES['Other'];
              const priorityCls = PRIORITY_STYLES[priority] || PRIORITY_STYLES['Medium'];
              const borderCls = PRIORITY_BORDER[priority] || PRIORITY_BORDER['Medium'];
              const isCollapsed = collapsedMessages.has(message.id);
              return (
                <Card
                  key={message.id}
                  className={`bg-gray-800 border-gray-700 ${borderCls}`}
                  data-testid={`ics213-message-${message.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <button
                        type="button"
                        onClick={() => toggleMessageCollapsed(message.id)}
                        className="mt-1 text-gray-400 hover:text-white transition-colors flex-shrink-0"
                        aria-label={isCollapsed ? 'Expand message' : 'Collapse message'}
                        data-testid={`ics213-toggle-${message.id}`}
                      >
                        {isCollapsed ? <ChevronRight className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <h3 className="text-lg font-semibold text-white truncate">
                            {message.subject || 'No Subject'}
                          </h3>
                          <Badge variant="outline" className="text-orange-500 border-orange-500/50 flex-shrink-0">
                            ICS 213
                          </Badge>
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full border flex-shrink-0 ${categoryCls}`}
                            data-testid={`ics213-category-badge-${message.id}`}
                          >
                            {category}
                          </span>
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full border flex-shrink-0 ${priorityCls}`}
                            data-testid={`ics213-priority-badge-${message.id}`}
                          >
                            {priority}
                          </span>
                        </div>
                        {isCollapsed ? (
                          <p className="text-gray-500 text-xs truncate">
                            {message.date || ''}{message.time ? ` ${message.time}` : ''} · From: {message.sender || message.from || 'Not specified'} · To: {message.to || 'Not specified'}
                          </p>
                        ) : (
                          <>
                            <div className="text-gray-400 text-sm space-y-1 mt-2">
                              <p><span className="font-medium">To:</span> {message.to || 'Not specified'}</p>
                              <p><span className="font-medium">From:</span> {message.sender || message.from || 'Not specified'}</p>
                              <p><span className="font-medium">Date:</span> {message.date || 'Not specified'} {message.time && `at ${message.time}`}</p>
                            </div>
                            {message.message && (
                              <p className="text-gray-300 mt-3 whitespace-pre-wrap">{message.message}</p>
                            )}
                          </>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Button
                          onClick={() => handleExportSingleCSV(message)}
                          variant="outline"
                          size="sm"
                          className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10"
                          data-testid={`ics213-export-row-${message.id}`}
                          title="Export this entry as WinLink-compatible CSV"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => handlePrintICS213(message)}
                          variant="outline"
                          size="sm"
                          className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                          data-testid={`ics213-print-${message.id}`}
                        >
                          <Printer className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => handleEdit(message)}
                          variant="outline"
                          size="sm"
                          className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                          data-testid={`ics213-edit-${message.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => handleDelete(message.id)}
                          variant="outline"
                          size="sm"
                          className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                          data-testid={`ics213-delete-${message.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ICS 214 Activity Log Form (Self-contained for ICS interface)
// ICSActivityLogForm moved to components/ICSActivityLog.js

// ICS215Form moved to components/ICS215Form.js

// ICS 216 - Radio Requirements Worksheet Form

export default ICSGeneralMessageForm;
